// frontend/src/utils/unitFormat.js
// Formateo CENTRAL de magnitudes según las preferencias de unidades del usuario.
//
// Regla del sistema: ningún componente decide por su cuenta cuántos decimales
// mostrar ni en qué unidad. Todo pasa por aquí, que lee `unitPreferences`
// (GET /unit-preferences/me) y respeta:
//   - unidad por métrica (pressure_unit, temperature_unit, speed_unit…)
//   - decimales por métrica (decimal_places_map / display_settings)
//   - decimales globales (decimal_places) como respaldo
//
// Los valores de entrada SIEMPRE vienen en la unidad canónica del backend:
// presión en PSI, temperatura en °C, velocidad en km/h, distancia en km.

export const UNIT_SYMBOLS = {
  PSI: 'PSI',
  BAR: 'bar',
  kPa: 'kPa',
  'kg/cm2': 'kg/cm²',
  atm: 'atm',
  hPa: 'hPa',
  mmHg: 'mmHg',
  inHg: 'inHg',
  C: '°C',
  F: '°F',
  K: 'K',
  R: '°R',
  'km/h': 'km/h',
  mph: 'mph',
  'm/s': 'm/s',
  G: 'G',
  km: 'km',
  mi: 'mi',
};

export const DEFAULT_UNIT_PREFERENCES = {
  pressure_unit: 'PSI',
  temperature_unit: 'C',
  acceleration_unit: 'G',
  speed_unit: 'km/h',
  decimal_places: '2',
  use_imperial_system: false,
};

export const getUnitSymbol = (unit) => UNIT_SYMBOLS[unit] || unit || '';

const isNumber = (value) => value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));

/** Decimales configurados para una métrica, con respaldo sensato. */
export const getDecimals = (prefs, metric, fallback) => {
  const global = parseInt(prefs?.decimal_places, 10);
  const globalDecimals = Number.isInteger(global) ? global : 2;
  const map = prefs?.decimal_places_map || prefs?.display_settings || {};
  const raw = map?.[metric];
  const value = Number.isInteger(raw) ? raw : (Number.isInteger(raw?.decimals) ? raw.decimals : undefined);
  if (Number.isInteger(value)) return value;
  if (Number.isInteger(fallback)) return fallback;
  return globalDecimals;
};

// ── conversiones (entrada canónica → unidad del usuario) ────────────────────

export const convertPressure = (psi, targetUnit = 'PSI') => {
  if (!isNumber(psi)) return null;
  const value = Number(psi);
  switch (targetUnit) {
    case 'BAR': return value / 14.5038;
    case 'kPa': return value / 0.145038;
    case 'kg/cm2': return value / 14.2233;
    case 'atm': return value / 14.6959;
    case 'hPa': return value / 0.0145038;
    case 'mmHg': return value * 51.7149;
    case 'inHg': return value * 2.03602;
    default: return value;
  }
};

export const convertTemperature = (celsius, targetUnit = 'C') => {
  if (!isNumber(celsius)) return null;
  const value = Number(celsius);
  switch (targetUnit) {
    case 'F': return (value * 9) / 5 + 32;
    case 'K': return value + 273.15;
    case 'R': return (value + 273.15) * 9 / 5;
    default: return value;
  }
};

export const convertSpeed = (kmh, targetUnit = 'km/h') => {
  if (!isNumber(kmh)) return null;
  const value = Number(kmh);
  switch (targetUnit) {
    case 'mph': return value / 1.609344;
    case 'm/s': return value / 3.6;
    default: return value;
  }
};

export const convertDistance = (km, prefs) => {
  if (!isNumber(km)) return null;
  const value = Number(km);
  return prefs?.use_imperial_system ? value / 1.609344 : value;
};

// ── formateo ────────────────────────────────────────────────────────────────

const withUnit = (text, unit, showUnit) => (showUnit && unit ? `${text} ${unit}` : text);

export const formatPressureValue = (psi, prefs, { showUnit = true, signed = false } = {}) => {
  if (!isNumber(psi)) return '—';
  const unit = prefs?.pressure_unit || 'PSI';
  const converted = convertPressure(psi, unit);
  const decimals = getDecimals(prefs, 'pressure');
  const text = converted.toFixed(decimals);
  return withUnit(signed && converted > 0 ? `+${text}` : text, getUnitSymbol(unit), showUnit);
};

export const formatTemperatureValue = (celsius, prefs, { showUnit = true } = {}) => {
  if (!isNumber(celsius)) return '—';
  const unit = prefs?.temperature_unit || 'C';
  const converted = convertTemperature(celsius, unit);
  const decimals = getDecimals(prefs, 'temperature', 1);
  const text = converted.toFixed(decimals);
  // La temperatura no lleva espacio antes del símbolo de grado.
  return showUnit ? `${text}${getUnitSymbol(unit)}` : text;
};

export const formatSpeedValue = (kmh, prefs, { showUnit = true } = {}) => {
  if (!isNumber(kmh)) return '—';
  const unit = prefs?.speed_unit || 'km/h';
  const converted = convertSpeed(kmh, unit);
  const decimals = getDecimals(prefs, 'speed', 0);
  return withUnit(converted.toFixed(decimals), getUnitSymbol(unit), showUnit);
};

export const formatDistanceValue = (km, prefs, { showUnit = true } = {}) => {
  if (!isNumber(km)) return '—';
  const converted = convertDistance(km, prefs);
  const decimals = getDecimals(prefs, 'distance', 1);
  const unit = prefs?.use_imperial_system ? 'mi' : 'km';
  return withUnit(converted.toFixed(decimals), unit, showUnit);
};

export const formatHoursValue = (hours, { showUnit = true } = {}) => {
  if (!isNumber(hours)) return '—';
  const value = Number(hours);
  return showUnit ? `${value.toFixed(1)} h` : value.toFixed(1);
};

/**
 * Etiqueta compacta del ajuste de presión: "⬆️ +8 PSI" / "⬇️ -5 PSI" / "✅ OK".
 * `status` y `delta` vienen del motor central del backend (tpms_action_plan);
 * aquí NO se recalcula nada, solo se formatea con las unidades del usuario.
 */
export const formatAdjustment = (status, delta, prefs) => {
  switch (status) {
    case 'subir':
      return { icon: '⬆️', text: `+${formatPressureValue(delta, prefs, { showUnit: true })}`, tone: 'warning' };
    case 'bajar':
      return { icon: '⬇️', text: `-${formatPressureValue(delta, prefs, { showUnit: true })}`, tone: 'info' };
    case 'ok':
      return { icon: '✅', text: 'OK', tone: 'success' };
    case 'sin_reporte':
      return { icon: '🛜', text: 'Revisar sensor', tone: 'neutral' };
    case 'sin_sensor':
      return { icon: '—', text: 'Sin sensor', tone: 'neutral' };
    default:
      return { icon: '', text: '—', tone: 'neutral' };
  }
};

const unitFormat = {
  UNIT_SYMBOLS,
  DEFAULT_UNIT_PREFERENCES,
  getUnitSymbol,
  getDecimals,
  convertPressure,
  convertTemperature,
  convertSpeed,
  convertDistance,
  formatPressureValue,
  formatTemperatureValue,
  formatSpeedValue,
  formatDistanceValue,
  formatHoursValue,
  formatAdjustment,
};

export default unitFormat;
